import React from 'react';
import {
  IconQueue,
  IconListPlay,
  IconShuffle,
  IconRepeat,
  IconMute,
  IconVolume,
  IconDownload,
  IconShare,
  IconTools
} from '../icons';

export default function SecondaryControlsRow({
  variant = 'desktop', // 'desktop' | 'mobile'
  cx,
  t,
  currentMusic,
  playlist,
  showPlaylist,
  setShowPlaylist,
  playMode,
  togglePlayMode,
  muted,
  volume,
  toggleMute,
  handleVolumeChange,
  showSettings,
  setShowSettings,
  settingsButtonRef,
  handleButtonClickWithHint,
  iconClass,
  iconButtonBase,
  iconButtonIdle,
  iconButtonActive,
  iconButtonMuted,
  iconButtonDisabled,
  isMdToLg,
  isLgUp,
  hideVolumeSlider,
  volumeSliderWrapRef,
  handleDownload,
  handleShare
}) {
  const isMobile = variant === 'mobile';

  if (!currentMusic) return null;

  if (isMobile) {
    return (
      <div className="md:hidden -mx-3 px-3 w-full overflow-x-auto" data-no-toggle>
        <div className="w-full flex justify-center">
          <div className="flex items-center gap-2 min-w-max whitespace-nowrap">

            {playlist && playlist.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newState = !showPlaylist;
                  handleButtonClickWithHint(e, () => setShowPlaylist(newState), t('player.playlist'));
                }}
                className={cx(iconButtonBase, showPlaylist ? iconButtonActive : iconButtonIdle)}
                title={t('player.playlist')}
                data-no-toggle
              >
                <IconQueue className={iconClass} />
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                const nextMode = playMode === 'sequential' ? 'random' : playMode === 'random' ? 'repeat' : 'sequential';
                const modeText = nextMode === 'sequential' ? t('player.sequential') : nextMode === 'random' ? t('player.random') : t('player.repeat');
                handleButtonClickWithHint(e, () => togglePlayMode(), modeText);
              }}
              className={cx(iconButtonBase, iconButtonIdle, iconButtonDisabled)}
              title={
                playMode === 'sequential' ? t('player.sequential') :
                playMode === 'random' ? t('player.random') :
                t('player.repeat')
              }
              data-no-toggle
            >
              {playMode === 'sequential' && (<IconListPlay className={iconClass} />)}
              {playMode === 'random' && (<IconShuffle className={iconClass} />)}
              {playMode === 'repeat' && (<IconRepeat className={iconClass} />)}
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleButtonClickWithHint(e, () => toggleMute(), muted ? t('player.unmute') : t('player.mute'));
              }}
              className={cx(iconButtonBase, (muted || volume === 0) ? iconButtonMuted : iconButtonIdle)}
              title={muted ? t('player.unmute') : t('player.mute')}
              data-no-toggle
            >
              {muted || volume === 0 ? (
                <IconMute className={iconClass} />
              ) : (
                <IconVolume className={iconClass} />
              )}
            </button>

            {currentMusic.allow_download && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleButtonClickWithHint(e, () => handleDownload(e), t('musicList.download'));
                }}
                className={cx(iconButtonBase, iconButtonIdle)}
                title={t('musicList.download')}
                data-no-toggle
              >
                <IconDownload className={iconClass} />
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleButtonClickWithHint(e, () => handleShare(e), t('musicList.share'));
              }}
              className={cx(iconButtonBase, iconButtonIdle)}
              title={t('musicList.share')}
              data-no-toggle
            >
              <IconShare className={iconClass} />
            </button>

            <button
              ref={settingsButtonRef}
              onClick={(e) => {
                e.stopPropagation();
                const newState = !showSettings;
                handleButtonClickWithHint(e, () => setShowSettings(newState), newState ? t('player.closeSettings') : t('player.openSettings'));
              }}
              className={cx(iconButtonBase, showSettings ? iconButtonActive : iconButtonIdle)}
              title={showSettings ? t('player.closeSettings') : t('player.openSettings')}
              data-no-toggle
            >
              <IconTools className={iconClass} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // desktop
  return (
    <div className="hidden md:flex items-center gap-2 md:gap-2.5 justify-center md:justify-start w-full md:w-auto min-w-0 md:flex-wrap lg:flex-nowrap lg:whitespace-nowrap lg:overflow-hidden">

      {playlist && playlist.length > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            const newState = !showPlaylist;
            handleButtonClickWithHint(e, () => setShowPlaylist(newState), t('player.playlist'));
          }}
          className={cx(iconButtonBase, showPlaylist ? iconButtonActive : iconButtonIdle)}
          title={t('player.playlist')}
          data-no-toggle
        >
          <IconQueue className={iconClass} />
        </button>
      )}

      <button
        onClick={(e) => {
          e.stopPropagation();
          const nextMode = playMode === 'sequential' ? 'random' : playMode === 'random' ? 'repeat' : 'sequential';
          const modeText = nextMode === 'sequential' ? t('player.sequential') : nextMode === 'random' ? t('player.random') : t('player.repeat');
          handleButtonClickWithHint(e, () => togglePlayMode(), modeText);
        }}
        className={cx(iconButtonBase, iconButtonIdle, iconButtonDisabled)}
        title={
          playMode === 'sequential' ? t('player.sequential') :
          playMode === 'random' ? t('player.random') :
          t('player.repeat')
        }
        data-no-toggle
      >
        {playMode === 'sequential' && (<IconListPlay className={iconClass} />)}
        {playMode === 'random' && (<IconShuffle className={iconClass} />)}
        {playMode === 'repeat' && (<IconRepeat className={iconClass} />)}
      </button>

      <div className="flex items-center gap-2 flex-shrink-0 justify-center md:justify-start" data-no-toggle>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleButtonClickWithHint(e, () => toggleMute(), muted ? t('player.unmute') : t('player.mute'));
          }}
          className={cx(iconButtonBase, (muted || volume === 0) ? iconButtonMuted : iconButtonIdle)}
          title={muted ? t('player.unmute') : t('player.mute')}
          data-no-toggle
        >
          {muted || volume === 0 ? (
            <IconMute className={iconClass} />
          ) : (
            <IconVolume className={iconClass} />
          )}
        </button>
        <span
          ref={volumeSliderWrapRef}
          className={(isMdToLg || (isLgUp && hideVolumeSlider)) ? 'hidden' : ''}
          data-no-toggle
        >
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={muted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-40 md:w-24 h-1.5 rounded-full accent-emerald-500 cursor-pointer"
            style={{ accentColor: '#10b981' }}
            data-no-toggle
          />
        </span>
      </div>

      {currentMusic.allow_download && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleButtonClickWithHint(e, () => handleDownload(e), t('musicList.download'));
          }}
          className={cx(iconButtonBase, iconButtonIdle)}
          title={t('musicList.download')}
          data-no-toggle
        >
          <IconDownload className={iconClass} />
        </button>
      )}

      <button
        onClick={(e) => {
          e.stopPropagation();
          handleButtonClickWithHint(e, () => handleShare(e), t('musicList.share'));
        }}
        className={cx(iconButtonBase, iconButtonIdle)}
        title={t('musicList.share')}
        data-no-toggle
      >
        <IconShare className={iconClass} />
      </button>

      <button
        ref={settingsButtonRef}
        onClick={(e) => {
          e.stopPropagation();
          const newState = !showSettings;
          handleButtonClickWithHint(e, () => setShowSettings(newState), newState ? t('player.closeSettings') : t('player.openSettings'));
        }}
        className={cx(iconButtonBase, showSettings ? iconButtonActive : iconButtonIdle)}
        title={showSettings ? t('player.closeSettings') : t('player.openSettings')}
        data-no-toggle
      >
        <IconTools className={iconClass} />
      </button>
    </div>
  );
}

